import { configureStore, type Middleware, type Reducer } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import type { DeepPartial } from 'utility-types'

import { reduxListenerMiddleware } from 'reduxTyped'
import type { ThunkExtra } from 'common/core/interfaces/actions'
import fetch from 'utils/middleware/fetch'
import fetchEndpoint from 'utils/middleware/fetchEndpoint'
import patchNotification from 'utils/middleware/patchNotification'

import rootReducer from '../reducers'

import { getBillingSentryContext } from './sentryBillingTransformer'

type PreloadedRootState = DeepPartial<RootState> | UnsafeAnyObject

interface CreateStoreOptions {
  reducer?: Reducer
  thunkExtraArgument?: ThunkExtra
  initialState?: PreloadedRootState
}
// TODO TC-1848 AnyAction deprecated in favour of UnknownAction
export function createStore({
  reducer = rootReducer,
  thunkExtraArgument,
  initialState = {},
}: CreateStoreOptions = {}): AppStore {
  const fetchOptions = { credentials: 'include' }

  const middlewares = [
    patchNotification() as Middleware,
    fetchEndpoint() as Middleware,
    fetch({ options: fetchOptions }) as Middleware,
    reduxListenerMiddleware.middleware,
  ].filter((middleware) => middleware !== null)

  const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    actionTransformer: (action) => ({ type: action.type }),
    stateTransformer: (state) => ({ billing: getBillingSentryContext(state) }),
  })

  return configureStore({
    reducer: reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: { extraArgument: thunkExtraArgument },
        immutableCheck: false,
        serializableCheck: false,
        actionCreatorCheck: false,
      }).concat(middlewares),
    preloadedState: initialState,
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  })
}
